import React from "react";
import Immutable from "immutable";

import {betterMemo} from "js/common/utils/more-memo";
import Select from "react-select";
import {CustomThemeContext} from "js/common/themes/CustomThemeProvider";
import TooltipLabel from "js/common/views/tooltip-label";
import {joinPathToString, stringToJoinPath} from "js/admin/kpis/edit-kpis/utils";

const Group = ({...props}) => {
  const {children, label, data} = props;
  const [expanded, setExpanded] = React.useState(data.defaultExpanded);
  const {theme} = React.useContext(CustomThemeContext);

  return (
      <div key={`group-${label.replace(" ", "-")}`}>
        <div>
          <div
              style={{
                display: "flex",
                padding: "5px 10px",
                fontSize: 13,
                alignItems: "center",
                justifyContent: "space-between",
                width: "100%",
                fontWeight: 500,
                fontFamily: theme.typography.fontFamilyBold,
                cursor: "pointer"
              }}
              onClick={() => !data.isSearching && setExpanded(!expanded)}>
            {label}
            {!data.isSearching && <i className={`bhi-${expanded ? "sort-asc" : "sort-desc"}`} />}
          </div>
        </div>

        <div style={{display: expanded || data.isSearching ? "block" : "none"}}>{children}</div>
      </div>
  );
};

const EntityAndServerColumnPicker = betterMemo(
    {displayName: "EntityColumnPicker"},
    ({
      columns,
      onColumnSelect,
      entityColumnId,
      joinPath,
      label,
      labelTooltip,
      disabled,
      placeholder,
      serverColumnName
    }) => {
      const {theme} = React.useContext(CustomThemeContext);
      const [isSearching, setIsSearching] = React.useState(false);

      const styles = {
        placeholder: (provided, state) => ({
          ...provided,
          fontSize: 14,
          color: state.isDisabled ? theme.palette.text.disabled : theme.palette.text.primary,
        }),
        input: (provided, state) => ({
          ...provided,
          color: theme.palette.text.main,
          fontFamily: `${theme.typography.fontFamily} !important`,
          input: {
            fontFamily: `${theme.typography.fontFamily} !important`,
            color: theme.palette.text.main,
          }
        }),
        control: (provided, state) => ({
          ...provided,
          backgroundColor: state.isDisabled ? theme.palette.action.hover : theme.palette.background.card,
          border: `2px solid ${theme.palette.background.paper}`,
          boxShadow: "none",
          minHeight: 0,
          height: 42,
          cursor: "pointer",
          fontSize: 14,
          ":hover": {
            backgroundColor: theme.palette.action.hover,
            border: `2px solid ${theme.palette.background.paper}`,
          }
        }),
        dropdownIndicator: (provided, state) => ({
          ...provided,
          color: state.isDisabled ? theme.palette.text.disabled : theme.palette.text.primary,
          ":hover": state.isDisabled ? theme.palette.text.disabled : theme.palette.text.primary
        }),
        indicatorSeparator: () => ({display: "none"}),
        menu: (provided) => ({
          ...provided,
          backgroundColor: `${theme.palette.background.card}`,
          zIndex: 9999,
          marginTop: 0,
          overflow: "auto"
        }),
        option: (provided, state) => ({
          ...provided,
          color: theme.palette.text.main,
          fontSize: 13,
          backgroundColor: state.isFocused
              ? theme.palette.background.paper
              : theme.palette.background.card,
        }),
        singleValue: (provided, state) => ({
          ...provided,
          fontSize: 13,
          color: `${state.isDisabled
              ? theme.palette.text.disabled
              : theme.palette.text.primary} !important`,
          padding: "3px 0"
        }),
      }

      const selectedValueToExpand = React.useMemo(() => {
        let lookupValue;
        if(serverColumnName) {
          lookupValue = columns.find(c => c.get("name") === serverColumnName);
        } else {
          lookupValue = columns.find(c => c.get("entityColumnId") === entityColumnId);
        }
        return lookupValue;
      }, [serverColumnName, entityColumnId, columns])

      const processedColumns = React.useMemo(() => {
        return columns
            .toSet()
            .filter(c => c.get("isVisibleEntityColumn", true)) // Display only visible entity columns
            .union(selectedValueToExpand
                ? Immutable.Set([selectedValueToExpand]) // Reintroduce selected column in case it was filtered out
                : Immutable.Set())
            .sortBy(c => c.get("label"));
      }, [selectedValueToExpand, columns]);

      const groupedOptions = React.useMemo(() => {
        const groupOptions = [];
        const generateRecursiveGroup = (columns, currentDepth, isSearching) => {
          const childGroups = columns
              .filter(c => c.get("readableJoinPath").count() > currentDepth)
              .groupBy(c => c.get("readableJoinPath").get(currentDepth))
          childGroups
              .entrySeq()
              .sortBy(([entity]) => entity)
              .forEach(([entity, columns]) => {
                const columnsAtCurrentDepth = columns.filter(g => g.get("readableJoinPath").count() === currentDepth + 1)
                if (columnsAtCurrentDepth.count() > 0) {
                  const firstGroup = currentDepth === 0;
                  const label = firstGroup ? entity : columnsAtCurrentDepth.first().get("readableJoinPath").slice(1).join(' > ');
                  const originalLabel = firstGroup ? entity : columnsAtCurrentDepth.first().get("readableJoinPath").last();
                  const labelIsExpanded = (serverColumnName || entityColumnId) ? selectedValueToExpand?.get("readableJoinPath").last() === originalLabel : firstGroup;
                  const child = {
                    label,
                    defaultExpanded: labelIsExpanded,
                    isSearching: isSearching,
                    options: columnsAtCurrentDepth
                        .map(column => {
                              let uniqueId;
                              let idOrName;
                              if(column.get("columnType") === "SERVER_COLUMN") {
                                uniqueId = column.get("name");
                                idOrName = uniqueId;
                              } else {
                                uniqueId = `${column.get("joinPathStr")}:${column.get("entityColumnId")}`;
                                idOrName = column.get("entityColumnId");
                              }
                              return {
                                colId: idOrName,
                                uniqueId: uniqueId,
                                joinPath: column.get("joinPathStr"),
                                label: column.get("label"),
                                dataType: column.get("dataType"),
                                columnType: column.get("columnType")
                              }
                        }).toJS()
                  };
                  groupOptions.push(child);
                }
                generateRecursiveGroup(columns, currentDepth + 1, isSearching)
              })
        };
        processedColumns && generateRecursiveGroup(processedColumns, 0, isSearching)
        return groupOptions;
      }, [entityColumnId, isSearching, processedColumns, selectedValueToExpand, serverColumnName])

      const selectedOption = React.useMemo(() => {
        const pathStr = joinPathToString(joinPath);
        if (joinPath && entityColumnId) {
          return groupedOptions.map(g => g.options).flat().find(o => {
            return o.uniqueId === `${pathStr}:${entityColumnId}`
          })
        } else if (serverColumnName && !entityColumnId && !joinPath) {
          return groupedOptions.map(g => g.options).flat().find(o => {
            return o.uniqueId === serverColumnName;
          })
        } else {
          return null;
        }
      }, [groupedOptions, joinPath, entityColumnId, serverColumnName])

      // NOTE: The below function replaces the default react-select search behaviour
      // we need this because react-select does not have built in behaviour to search by group name/label
      const filterOption = ({label, data: { joinPath }}, string) => {
        label = label.toLocaleLowerCase();
        string = string.toLocaleLowerCase();
        if (label.includes(string) || joinPath?.includes(string)) return true;

        const groupOptions = groupedOptions.filter(group =>
            group.label.toLocaleLowerCase().includes(string)
        );

        if (groupOptions) {
          for (const groupOption of groupOptions) {
            const option = groupOption.options.find(opt => opt.joinPath === joinPath);
            if (option) {
              return true;
            }
          }
        }
        return false;
      };

      return <>
        {label && <TooltipLabel label={label} tooltip={labelTooltip}/>}
        <Select
          options={groupedOptions}
          searchable
          onInputChange={x => setIsSearching( x.length > 0)}
          blurInputOnSelect={false}
          closeMenuOnSelect={true}
          filterOption={filterOption}
          components={{Group}}
          styles={styles}
          value={selectedOption || ""}
          onChange={({colId, joinPath, dataType, columnType}) => onColumnSelect(colId, stringToJoinPath(joinPath), dataType, columnType)}
          isDisabled={disabled}
          placeholder={placeholder}
      />
        </>;
    });

export default EntityAndServerColumnPicker;
